import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import PaginationData from 'types/models/PaginationData';
import TwitterConfig from 'types/models/TwitterConfig';
import EditTwitterConfigContext from 'contexts/EditTwitterConfigContext';
import AppContext from 'contexts/AppContext';
import { getTwitterConfigs } from 'services/twitterConfigsServices';
import TwitterSearchType from 'types/enums/TwitterSearchType';
import SearchBar from 'components/SearchBar';
import useDebounce from 'hooks/useDebounce';

export default (): JSX.Element => {
  const history = useHistory();
  const { currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const [paginationTwitterConfigs, setPaginationTwitterConfigs] = React.useState<PaginationData<TwitterConfig>>();
  const [searchValue, setSearchValue] = React.useState('');
  const { setSelectedTwitterConfig } = React.useContext(EditTwitterConfigContext);
  const { t } = useTranslation();

  const loadTwitterConfigs = React.useCallback(async (page?: number, seachTerm?: string) => {
    const paginationData = await getTwitterConfigs(page, seachTerm);
    if (paginationData) {
      setPaginationTwitterConfigs(paginationData);
    }
  }, []);

  useDebounce(() => {
    if (searchValue.trim()) {
      loadTwitterConfigs(undefined, searchValue);
    } else {
      loadTwitterConfigs();
    }
  }, [searchValue]);

  React.useEffect(() => {
    if (currentPageListNumber) {
      loadTwitterConfigs(currentPageListNumber);
    }
  }, [currentPageListNumber]);
  const renderEditButton = (twitterConfig: TwitterConfig | null): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          setSelectedTwitterConfig(twitterConfig);
          history.push('twitter-configs/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: TwitterConfig[]): JSX.Element => {
    return (
      <>
        {paginationData.map((twitterConfig: TwitterConfig) => (
          <TableRow key={twitterConfig.id}>
            <TableCell component="th" scope="row">
              {t('listTwitterConfig-page-type')}: {TwitterSearchType[twitterConfig.searchType]} |{' '}
              {t('listTwitterConfig-page-value')}: {twitterConfig.value}
            </TableCell>
            <TableCell>{renderEditButton(twitterConfig)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      <SearchBar
        label={t('search-label')}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onClear={() => {
          setSearchValue('');
        }}
      />

      <Button
        className="btn-50"
        variant="contained"
        color="primary"
        onClick={() => {
          history.push('/private/admin/twitter-configs/create');
        }}
      >
        {t('listTwitterConfig-page-create')}
      </Button>
      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('listTwitterConfig-page-description')}</TableCell>
              <TableCell>{t('listTwitterConfig-page-edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationTwitterConfigs?.data && paginationTwitterConfigs?.data.length > 0 ? (
              renderContent(paginationTwitterConfigs.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listTwitterConfig-page-noCategories')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationTwitterConfigs && (
        <TablePagination
          component="div"
          count={paginationTwitterConfigs.total}
          page={paginationTwitterConfigs.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
            loadTwitterConfigs(page + 1);
          }}
          rowsPerPage={paginationTwitterConfigs.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
};
